import { lazy, Suspense, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { SiteHeader } from '@/components/SiteHeader';
import { RodapeTemplate } from '@/components/template/RodapeTemplate';
import { LogoMPC } from '@/components/LogoMPC';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ScrollToTop } from '@/components/ScrollToTop';
import { Preloader, devePreCarregar } from '@/components/Preloader';
import { PageShell, Skeleton } from '@/components/ui';
import { HomePage } from '@/pages/HomePage';

/**
 * A home entra no bundle principal — é a primeira tela de quase todo visitante.
 * As demais são carregadas sob demanda: o painel admin, em especial, é peso
 * morto para os 99% que só querem ver a tabela do grupo.
 */
const TournamentsPage = lazy(() =>
  import('@/pages/TournamentsPage').then((m) => ({ default: m.TournamentsPage })),
);
const TournamentPage = lazy(() =>
  import('@/pages/TournamentPage').then((m) => ({ default: m.TournamentPage })),
);
const MatchPage = lazy(() => import('@/pages/MatchPage').then((m) => ({ default: m.MatchPage })));
const TeamsPage = lazy(() => import('@/pages/TeamsPage').then((m) => ({ default: m.TeamsPage })));
const TeamPage = lazy(() => import('@/pages/TeamsPage').then((m) => ({ default: m.TeamPage })));
const StatsPage = lazy(() => import('@/pages/StatsPage').then((m) => ({ default: m.StatsPage })));
const RankingsPage = lazy(() => import('@/pages/RankingsPage').then((m) => ({ default: m.RankingsPage })));
const LojaPage = lazy(() => import('@/pages/LojaPage').then((m) => ({ default: m.LojaPage })));
const MoedasPage = lazy(() => import('@/pages/admin/MoedasPage').then((m) => ({ default: m.MoedasPage })));
const MinhasPartidasPage = lazy(() => import('@/pages/capitao/MinhasPartidasPage'));
const EnvioDeSumulaPage = lazy(() => import('@/pages/capitao/EnvioDeSumulaPage'));
const CadastroTimePage = lazy(() =>
  import('@/pages/CadastroTimePage').then((m) => ({ default: m.CadastroTimePage })),
);
const ConvitePage = lazy(() =>
  import('@/pages/ConvitePage').then((m) => ({ default: m.ConvitePage })),
);
const AuthPage = lazy(() => import('@/pages/AuthPage').then((m) => ({ default: m.AuthPage })));
const EsqueciSenhaPage = lazy(() =>
  import('@/pages/EsqueciSenhaPage').then((m) => ({ default: m.EsqueciSenhaPage })),
);
const RedefinirSenhaPage = lazy(() =>
  import('@/pages/RedefinirSenhaPage').then((m) => ({ default: m.RedefinirSenhaPage })),
);
const RegulamentoPage = lazy(() =>
  import('@/pages/RegulamentoPage').then((m) => ({ default: m.RegulamentoPage })),
);
const SuportePage = lazy(() =>
  import('@/pages/SuportePage').then((m) => ({ default: m.SuportePage })),
);
const CaptainTeamPage = lazy(() =>
  import('@/pages/captain/TeamPage').then((m) => ({ default: m.CaptainTeamPage })),
);
const CreateTeamPage = lazy(() =>
  import('@/pages/captain/TeamPage').then((m) => ({ default: m.CreateTeamPage })),
);
const RegisterTeamPage = lazy(() =>
  import('@/pages/captain/TeamPage').then((m) => ({ default: m.RegisterTeamPage })),
);
const PainelPage = lazy(() =>
  import('@/pages/admin/PainelPage').then((m) => ({ default: m.PainelPage })),
);
const AdminHomePage = lazy(() =>
  import('@/pages/admin/AdminHomePage').then((m) => ({ default: m.AdminHomePage })),
);
const TournamentFormPage = lazy(() =>
  import('@/pages/admin/TournamentForm').then((m) => ({ default: m.TournamentFormPage })),
);
const AdminTicketsPage = lazy(() =>
  import('@/pages/admin/TicketsPage').then((m) => ({ default: m.AdminTicketsPage })),
);
const AdminTournamentPage = lazy(() =>
  import('@/pages/admin/AdminTournamentPage').then((m) => ({ default: m.AdminTournamentPage })),
);
const UsersPage = lazy(() =>
  import('@/pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const CollectorPage = lazy(() =>
  import('@/pages/admin/CollectorPage').then((m) => ({ default: m.CollectorPage })),
);

import { RequireRole } from '@/components/RequireRole';

export function App() {
  /*
   * A decisão de mostrar a cortina é tomada uma vez, na montagem.
   *
   * Ela precisa de `useState` com inicializador, e não de uma variável solta:
   * `devePreCarregar()` lê `sessionStorage`, e reavaliar isso a cada render
   * faria a cortina sumir no meio do carregamento — a própria tela grava a
   * marca da sessão quando termina.
   */
  const [preCarregando, setPreCarregando] = useState(devePreCarregar);

  /*
   * A home é a única rota desenhada com o layout do template, e por isso a
   * única que dispensa a moldura do produto.
   */
  const { pathname } = useLocation();
  const naHome = pathname === '/';

  /*
   * A área do capitão (link do time) é fechada: sem menu, sem ações de conta,
   * sem rodapé, e o logo não leva a lugar nenhum. Quem entra pelo link do time
   * só tem o que fazer ali -- e não deve sair navegando o site com a sessão
   * do time aberta.
   */
  const areaDoCapitao = pathname.startsWith('/minhas-partidas');

  return (
    <div className="flex min-h-svh flex-col bg-ink">
      {/* Fica fora do `main` e por cima de tudo: é uma cortina, não uma rota. */}
      {preCarregando && !areaDoCapitao && <Preloader onPronto={() => setPreCarregando(false)} />}
      <ScrollToTop />
      {/*
        O cabeçalho é o mesmo em todas as rotas, a home inclusive. A home só
        traz o próprio rodapé, no layout do template, com o halo.
      */}
      {areaDoCapitao ? (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-ink/95">
          <div className="mx-auto flex h-18 max-w-mpc items-center px-6">
            <LogoMPC altura={46} />
          </div>
        </header>
      ) : (
        <SiteHeader />
      )}
      {/*
        O conteúdo ocupa no mínimo uma tela inteira. Numa página curta — uma
        aba com uma edição só, um formulário — o rodapé subia e aparecia na
        primeira dobra, como se fosse parte do corpo da página. Com a altura
        mínima ele fica sempre abaixo da dobra, e só aparece quando a pessoa
        rola até o fim. `svh`, e não `vh`, para não contar a barra do navegador
        do celular, que some e reaparece durante a rolagem.
      */}
      <main className="min-h-svh flex-1">
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/campeonatos" element={<TournamentsPage />} />
              <Route path="/campeonatos/:slug" element={<TournamentPage />} />
              {/* `/classificacao` nunca mostrou classificação: apontava para a
                  StatsPage, que lista artilharia e assistências. A tabela do
                  grupo vive na aba do campeonato, então o endereço antigo passa
                  a levar para a escolha da edição em vez de mentir — e links
                  antigos continuam funcionando em vez de cair em 404. */}
              <Route path="/classificacao" element={<Navigate to="/campeonatos" replace />} />
              <Route path="/partidas/:id" element={<MatchPage />} />
              <Route path="/times" element={<TeamsPage />} />
              {/* Cadastro de equipe: publico, e e por aqui que se entra na
                  Hexa Cup. Cria a conta do capitao junto com o time. */}
              <Route path="/cadastrar-time" element={<CadastroTimePage />} />
              {/* Convite para assumir um time que a organizacao cadastrou. */}
              <Route path="/convite/:token" element={<ConvitePage />} />
              <Route path="/times/:slug" element={<TeamPage />} />
              <Route path="/estatisticas" element={<StatsPage />} />
              <Route path="/rankings" element={<RankingsPage />} />
              <Route path="/loja" element={<LojaPage />} />
              <Route path="/minhas-partidas/:slug" element={<MinhasPartidasPage />} />
              <Route path="/minhas-partidas/:slug/partida/:matchId" element={<EnvioDeSumulaPage />} />
            <Route path="/entrar" element={<AuthPage />} />
            <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
            <Route path="/redefinir-senha/:token" element={<RedefinirSenhaPage />} />
            <Route path="/regulamento" element={<RegulamentoPage />} />
            <Route path="/suporte" element={<SuportePage />} />
            <Route
              path="/campeonatos/:slug/inscricao"
              element={
                <RequireRole role="player">
                  <RegisterTeamPage />
                </RequireRole>
              }
            />
            <Route
              path="/meu-time/novo"
              element={
                <RequireRole role="player">
                  <CreateTeamPage />
                </RequireRole>
              }
            />
            <Route
              path="/meu-time/:id"
              element={
                <RequireRole role="player">
                  <CaptainTeamPage />
                </RequireRole>
              }
            />
            {/* Rotas administrativas da organização */}
            <Route
              path="/admin"
              element={
                <RequireRole role="staff">
                  <PainelPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/campeonatos"
              element={
                <RequireRole role="staff">
                  <AdminHomePage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/usuarios"
              element={
                <RequireRole role="admin">
                  <UsersPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/coletor"
              element={
                <RequireRole role="staff">
                  <CollectorPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/moedas"
              element={
                <RequireRole role="staff">
                  <MoedasPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/tickets"
              element={
                <RequireRole role="staff">
                  <AdminTicketsPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/campeonatos/novo"
              element={
                <RequireRole role="staff">
                  <TournamentFormPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/campeonatos/:slug"
              element={
                <RequireRole role="staff">
                  <AdminTournamentPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/campeonatos/:slug/editar"
              element={
                <RequireRole role="staff">
                  <TournamentFormPage />
                </RequireRole>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </main>
    {/*
      O rodapé é o do template em todas as rotas, o mesmo da home. Havia um
      rodapé próprio do produto nas páginas internas, com outra grade e outra
      tipografia, e ele era metade da sensação de "dois sites". Ele precisa da
      classe raiz do template para achar os tokens e as medidas dele.
    */}
    {!naHome && !areaDoCapitao && (
      <div className="mpc-template mpc-rodape-largo">
        <RodapeTemplate />
      </div>
    )}
  </div>
);
}

function RouteFallback() {
  return (
    <PageShell>
      <Skeleton className="h-16 w-full max-w-md" />
      <Skeleton className="mt-8 h-96 w-full" />
    </PageShell>
  );
}

function NotFound() {
  const location = useLocation();
  return (
    <PageShell>
      <div className="py-20 text-center">
        <p className="mpc-display text-[clamp(3rem,10vw,6rem)] text-brand-ink">404</p>
        <h1 className="mpc-display mt-2 text-2xl">Essa página não existe</h1>
        <p className="mt-2 text-sm text-muted">
          Nada encontrado em <code className="font-mono text-faint">{location.pathname}</code>
        </p>
        <Link
          to="/"
          className="mt-8 inline-block bg-brand px-6 py-3 font-medium text-black transition-colors hover:bg-brand-bright"
        >
          Voltar para o início
        </Link>
      </div>
    </PageShell>
  );
}
